/**
 * Media Stream Tracker
 *
 * Intercepts navigator.mediaDevices.getUserMedia to track all active MediaStreams.
 * This is necessary because third-party SDKs (like Jaak) use closed Shadow DOM,
 * making it impossible to access their <video> elements to stop camera streams.
 *
 * IMPORTANT: This module must be imported at the app entry point (main.tsx)
 * BEFORE any SDK loads, so the interceptor captures all getUserMedia calls.
 */

const activeStreams = new Set<MediaStream>();

const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);

navigator.mediaDevices.getUserMedia = async (
  constraints?: MediaStreamConstraints
): Promise<MediaStream> => {
  const stream = await originalGetUserMedia(constraints);
  activeStreams.add(stream);

  // Auto-remove when all tracks end naturally
  stream.getTracks().forEach(track => {
    track.addEventListener('ended', () => {
      const allEnded = stream.getTracks().every(t => t.readyState === 'ended');
      if (allEnded) activeStreams.delete(stream);
    });
  });

  return stream;
};

/**
 * Stops all tracked active MediaStream tracks and clears the registry.
 * Call this when navigating away from camera-using pages.
 */
export function stopAllMediaStreams(): void {
  activeStreams.forEach(stream => {
    stream.getTracks().forEach(track => track.stop());
  });
  activeStreams.clear();
}
